import { Printer as PrinterIcon, ReceiptText } from 'lucide-react'
import moment from 'moment'
import { Link } from 'react-router'
import { toast } from 'sonner'
import useSWR from 'swr'

import { Money, StatStrip } from '@/components/ds'
import { EetStatusBadge } from '@/components/Eet/EetStatusBadge'
import { EmptyState } from '@/components/EmptyState'
import { Button } from '@/components/ui/button'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetTitle,
} from '@/components/ui/sheet'
import { Skeleton } from '@/components/ui/skeleton'
import {
  formatTime,
  lineLabel,
  printReceiptOnUsb,
  receiptCurrency,
  receiptPaymentLabel,
  receiptPermalink,
  receiptTotal,
} from '@/containers/Receipts/receiptUtils'
import { apiFetcher } from '@/data/fetcher'
import { Collection } from '@/interfaces/Collection'
import { EetSubmissionCollection } from '@invo.space/shared'
import { Receipt } from '@invo.space/shared'
import { Summary } from '@invo.space/shared'
import { Printer } from '@/lib/printer/printer'

import { plural } from './registerUtils'

interface TodayReceiptsSheetProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  summary?: Summary
  printer: Printer | null
}

/** Dnešní účtenky přímo z pokladny, s rychlým opakovaným tiskem. */
export function TodayReceiptsSheet({
  open,
  onOpenChange,
  summary,
  printer,
}: TodayReceiptsSheetProps) {
  const today = moment().format('YYYY-MM-DD')
  const { data: receipts, isLoading } = useSWR<Collection<Receipt>>(
    open ? `/api/v2/receipts?date=${today}&limit=50` : null,
    apiFetcher,
  )
  const { data: eetSubmissions } = useSWR<EetSubmissionCollection>(
    open ? `/api/v2/eet-submissions?date=${today}&limit=100` : null,
    apiFetcher,
  )
  const empty = !isLoading && receipts?.data.length === 0
  const eetByReceiptId = Object.fromEntries(
    (eetSubmissions?.data ?? []).map((s) => [s.receiptId, s]),
  )

  const reprint = (receipt: Receipt) => {
    if (printer?.isBrowserSupported()) {
      printReceiptOnUsb(printer, receipt).catch((e: Error) =>
        toast.error(e.message || 'Tisk se nepodařil'),
      )
    } else {
      window.open(receiptPermalink(receipt), '_blank', 'noreferrer')
    }
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        className="flex flex-col"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <div className="border-b px-6 py-4 pr-12">
          <SheetTitle className="text-sm font-medium">
            Dnešní účtenky
          </SheetTitle>
          <SheetDescription className="text-xs">
            {moment().format('D. M. YYYY')}
          </SheetDescription>
        </div>

        {empty ? (
          <div className="grid flex-1 place-items-center px-6">
            <EmptyState
              compact
              icon={ReceiptText}
              title="Dnes zatím žádná účtenka"
              description="Po prvním prodeji tu uvidíte dnešní tržbu, platby v hotovosti a účtenky můžete vytisknout znovu."
              ctaButton={
                <Button
                  variant="outline"
                  className="mt-1"
                  onClick={() => onOpenChange(false)}
                >
                  Zpět k prodeji
                </Button>
              }
            />
          </div>
        ) : (
          <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-6 py-5">
            {summary && (
              <StatStrip
                items={[
                  {
                    label: 'Tržba dnes',
                    value: (
                      <Money
                        amount={summary.total.total.amount}
                        currency={summary.total.total.currency}
                        size="lg"
                      />
                    ),
                    hint: receipts
                      ? `${receipts.total} ${plural(receipts.total, 'účtenka', 'účtenky', 'účtenek')}`
                      : undefined,
                  },
                  {
                    label: 'Hotově',
                    value: (
                      <Money
                        amount={summary.byType.Cash?.total.amount ?? '0'}
                        currency={summary.total.total.currency}
                      />
                    ),
                  },
                ]}
              />
            )}

            <ul className="divide-y border-y">
              {isLoading &&
                Array.from({ length: 5 }).map((_, i) => (
                  <li key={i} className="py-3">
                    <Skeleton className="h-4 w-full" />
                  </li>
                ))}
              {receipts?.data.map((receipt) => (
                <li key={receipt.id} className="flex items-center gap-3 py-2">
                  <span className="w-11 shrink-0 text-sm text-muted-foreground tabular">
                    {formatTime(receipt.issuedAt)}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm">
                      {receipt.receiptLines.map(lineLabel).join(', ')}
                    </p>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      {receiptPaymentLabel[receipt.paymentType] ??
                        receipt.paymentType}
                      <EetStatusBadge
                        submission={eetByReceiptId[receipt.id]}
                        showPok={false}
                      />
                    </div>
                  </div>
                  <Money
                    amount={receiptTotal(receipt)}
                    currency={receiptCurrency(receipt)}
                  />
                  <Button
                    variant="ghost"
                    size="icon"
                    className="size-11"
                    onClick={() => reprint(receipt)}
                    aria-label="Vytisknout znovu"
                    title="Vytisknout znovu"
                  >
                    <PrinterIcon />
                  </Button>
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="border-t px-6 py-4">
          <Button variant="outline" className="h-11 w-full" asChild>
            <Link to="/cash-register/receipts">Všechny účtenky</Link>
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  )
}
